import { Fragment, useState } from 'react';
import {
  Ban,
  ChevronDown,
  ChevronRight,
  CircleCheck,
  Compass,
  Download,
  ExternalLink,
  ListPlus,
  Loader2,
  MoreVertical,
  Play,
  Search,
  RotateCcw,
  WifiOff,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import Pagination from '@/client/components/common/Pagination';
import { cn } from '@/lib/utils';
import { getApiErrorMessage } from '@/client/utils/errors';
import { formatDate } from '@/client/utils/datetime';
import type {
  ApiDiscoveryCandidate,
  DiscoveryKind,
  DiscoveryStatus,
} from '@/client/types/marketing/api';
import {
  useDiscoveryQuery,
  useImportDiscoveryMutation,
  useSearchDiscoveryMutation,
  useRunDiscoveryMutation,
  useUpdateDiscoveryMutation,
} from '@/client/api/marketing/discovery';
import { MarketingPageShell } from './components/MarketingPageShell';

const STATUS_TABS: { value: DiscoveryStatus; label: string }[] = [
  { value: 'new', label: 'New' },
  { value: 'queued', label: 'Queued' },
  { value: 'created', label: 'Created' },
  { value: 'duplicate', label: 'Duplicate' },
  { value: 'dismissed', label: 'Dismissed' },
];

const KIND_OPTIONS: { value: DiscoveryKind | 'all'; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'place', label: 'Place' },
  { value: 'event', label: 'Event' },
  { value: 'experience', label: 'Experience' },
];

const KIND_BADGE: Record<DiscoveryKind, string> = {
  place: 'bg-sky-50 text-sky-700',
  event: 'bg-violet-50 text-violet-700',
  experience: 'bg-amber-50 text-amber-800',
};

const listingUrl = (eid: number | string) => `https://feelyfeely.com/listing/${eid}`;

const detail = (c: ApiDiscoveryCandidate, key: string): string => {
  const v = c.details?.[key];
  return v == null || typeof v === 'object' ? '' : String(v);
};

/** One-line summary of the free-form details, by kind. */
const summary = (c: ApiDiscoveryCandidate): string => {
  const keys =
    c.kind === 'event'
      ? ['date', 'venue']
      : c.kind === 'experience'
        ? ['operator', 'price']
        : ['category'];
  return keys.map((k) => detail(c, k)).filter(Boolean).join(' · ');
};

type ActionKind = 'import' | 'search' | 'queue' | 'created' | 'dismiss' | 'restore';

const ACTION_COPY: Record<ActionKind, { title: string; confirm: string; body: string }> = {
  import: {
    title: 'Import as a listing?',
    confirm: 'Import',
    body: 'Creates the place listing in Review from the Google Maps record we already hold. No paid search. It still needs photos and approval before it goes live.',
  },
  search: {
    title: 'Search Google Maps and import?',
    confirm: 'Search and import',
    body: 'Uses 1 paid Google Maps search (SearchAPI; capped per day). On a clear name-and-area match it creates the listing in Review; otherwise nothing is created.',
  },
  queue: {
    title: 'Queue for import?',
    confirm: 'Queue',
    body: 'Marks this candidate as queued so it is picked up for import.',
  },
  created: {
    title: 'Mark as created',
    confirm: 'Mark created',
    body: 'Enter the id (eid) of the listing that was created from this candidate.',
  },
  dismiss: {
    title: 'Dismiss candidate?',
    confirm: 'Dismiss',
    body: 'Hides it from the New list. You can restore it later.',
  },
  restore: {
    title: 'Restore to New?',
    confirm: 'Restore',
    body: 'Puts this candidate back in the New list.',
  },
};

const DiscoveryPage = () => {
  const [status, setStatus] = useState<DiscoveryStatus>('new');
  const [kind, setKind] = useState<DiscoveryKind | 'all'>('all');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [open, setOpen] = useState<string | null>(null);
  const [action, setAction] = useState<{ item: ApiDiscoveryCandidate; kind: ActionKind } | null>(null);
  const [text, setText] = useState('');

  const { data, isLoading, isError, error } = useDiscoveryQuery({
    status,
    limit,
    offset: (page - 1) * limit,
    ...(kind !== 'all' ? { kind } : {}),
  });
  const update = useUpdateDiscoveryMutation();
  const importPlace = useImportDiscoveryMutation();
  const searchPlace = useSearchDiscoveryMutation();
  const run = useRunDiscoveryMutation();

  const items = data?.items ?? [];

  const startAction = (item: ApiDiscoveryCandidate, k: ActionKind) => {
    setText('');
    setAction({ item, kind: k });
  };

  const confirmAction = async () => {
    if (!action) return;
    const { item, kind: k } = action;
    if (k === 'import' || k === 'search') {
      try {
        const res =
          k === 'search'
            ? await searchPlace.mutateAsync({ id: item.id, confirm: true })
            : await importPlace.mutateAsync({ id: item.id });
        const body = res?.data as { error?: string; error_description?: string; created_eid?: number } | undefined;
        if (body?.error) throw new Error(body.error_description || body.error);
        toast.success(`${item.name}: listing ${body?.created_eid ?? ''} created in Review`);
        setAction(null);
      } catch (e) {
        toast.error(getApiErrorMessage(e, 'Could not import the place'));
      }
      return;
    }
    const eid = Number(text.trim());
    if (k === 'created' && (!Number.isInteger(eid) || eid <= 0)) {
      toast.error('Enter a numeric listing id');
      return;
    }
    const payload =
      k === 'queue'
        ? { status: 'queued' as const }
        : k === 'created'
          ? { status: 'created' as const, eid }
          : k === 'dismiss'
            ? { status: 'dismissed' as const, ...(text.trim() ? { note: text.trim() } : {}) }
            : { status: 'new' as const };
    try {
      const res = await update.mutateAsync({ id: item.id, ...payload });
      const err = (res?.data as { error?: string } | undefined)?.error;
      if (err) throw new Error(err);
      toast.success(`${item.name}: ${ACTION_COPY[k].confirm.toLowerCase()} done`);
      setAction(null);
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Could not update the candidate'));
    }
  };

  const runNow = async () => {
    try {
      await run.mutateAsync({});
      toast.success('Discovery queued. New candidates appear as it finishes.');
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Could not start discovery'));
    }
  };

  const selectStatus = (s: DiscoveryStatus) => {
    setStatus(s);
    setPage(1);
    setOpen(null);
  };

  const th = 'px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500';

  return (
    <MarketingPageShell
      title="Discovery"
      description="Places, events and experiences your tracked sources post about that are not listed yet. Queue them for import, mark them created, or dismiss them."
      crumbs={[{ label: 'Marketing' }, { label: 'Discovery' }]}
      icon={Compass}
      actions={
        <Button size="sm" onClick={runNow} disabled={run.isPending}>
          {run.isPending ? (
            <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
          ) : (
            <Play className="mr-1.5 h-4 w-4" />
          )}
          Run now
        </Button>
      }
    >
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex rounded-md border border-gray-200 bg-white p-0.5" role="group" aria-label="Status">
          {STATUS_TABS.map((t) => (
            <button
              key={t.value}
              type="button"
              onClick={() => selectStatus(t.value)}
              className={cn(
                'rounded px-2.5 py-1.5 text-xs font-medium transition-colors',
                status === t.value ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50',
              )}
            >
              {t.label}
              <span className="ml-1.5 opacity-70">{data?.counts?.[t.value] ?? '…'}</span>
            </button>
          ))}
        </div>
        <div className="flex rounded-md border border-gray-200 bg-white p-0.5" role="group" aria-label="Kind">
          {KIND_OPTIONS.map((o) => (
            <button
              key={o.value}
              type="button"
              onClick={() => {
                setKind(o.value);
                setPage(1);
              }}
              className={cn(
                'rounded px-2.5 py-1.5 text-xs font-medium transition-colors',
                kind === o.value ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50',
              )}
            >
              {o.label}
            </button>
          ))}
        </div>
      </div>

      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="w-8 px-2 py-3" />
                <th className={th}>Name</th>
                <th className={th}>Kind</th>
                <th className={th}>Area</th>
                <th className={th}>Details</th>
                <th className={th}>Mentions</th>
                <th className={th}>Last seen</th>
                <th className={cn(th, 'text-right')}>Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isLoading && (
                <tr>
                  <td colSpan={8} className="px-6 py-16 text-center text-sm text-gray-500">
                    <Loader2 className="mr-2 inline h-4 w-4 animate-spin" />
                    Loading candidates…
                  </td>
                </tr>
              )}
              {!isLoading && isError && (
                <tr>
                  <td colSpan={8} className="px-6 py-16 text-center text-sm">
                    <WifiOff className="mb-2 inline h-5 w-5 text-amber-600" />
                    <div className="font-medium text-amber-800">Could not load candidates</div>
                    <div className="mt-1 text-xs text-gray-500">
                      {getApiErrorMessage(error, 'The marketing API did not respond')}
                    </div>
                  </td>
                </tr>
              )}
              {!isLoading && !isError && items.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-6 py-16 text-center text-sm text-gray-500">
                    Nothing here. Discovery runs daily; use Run now to look again.
                  </td>
                </tr>
              )}
              {items.map((c) => {
                const expanded = open === c.id;
                const evidence = c.evidence ?? [];
                const extra = Object.entries(c.details ?? {});
                return (
                  <Fragment key={c.id}>
                    <tr className="cursor-pointer hover:bg-gray-50" onClick={() => setOpen(expanded ? null : c.id)}>
                      <td className="px-2 py-3 text-gray-400">
                        {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <span className="text-sm font-medium text-gray-900">{c.name}</span>
                          {c.google && (
                            <span
                              className="rounded bg-emerald-50 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700"
                              title={c.google.address || c.google.title}
                            >
                              Google cache
                            </span>
                          )}
                        </div>
                        {c.handle && (
                          <a
                            href={`https://instagram.com/${c.handle.replace(/^@/, '')}`}
                            target="_blank"
                            rel="noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="text-xs text-blue-700 hover:underline"
                          >
                            @{c.handle.replace(/^@/, '')}
                          </a>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className={cn('rounded-full px-2 py-0.5 text-xs font-medium capitalize', KIND_BADGE[c.kind])}>
                          {c.kind}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">{c.area || '–'}</td>
                      <td className="max-w-[16rem] truncate px-4 py-3 text-sm text-gray-600">{summary(c) || '–'}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {c.mentions ?? 0}
                        {!!c.authors?.length && (
                          <div className="max-w-[12rem] truncate text-xs text-gray-400" title={c.authors.join(', ')}>
                            {c.authors.join(', ')}
                          </div>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-600">{formatDate(c.last_seen)}</td>
                      <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button
                              type="button"
                              aria-label={`Actions for ${c.name}`}
                              className="rounded p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-800"
                            >
                              <MoreVertical className="h-4 w-4" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {c.kind === 'place' && c.google?.place_id && (c.status === 'new' || c.status === 'queued') && (
                              <DropdownMenuItem onSelect={() => startAction(c, 'import')}>
                                <Download className="mr-2 h-4 w-4" /> Import listing
                              </DropdownMenuItem>
                            )}
                            {c.kind === 'place' && !c.google?.place_id && (c.status === 'new' || c.status === 'queued') && (
                              <DropdownMenuItem onSelect={() => startAction(c, 'search')}>
                                <Search className="mr-2 h-4 w-4" /> Search and import
                              </DropdownMenuItem>
                            )}
                            {c.status === 'new' && (
                              <DropdownMenuItem onSelect={() => startAction(c, 'queue')}>
                                <ListPlus className="mr-2 h-4 w-4" /> Queue for import
                              </DropdownMenuItem>
                            )}
                            {(c.status === 'new' || c.status === 'queued') && (
                              <DropdownMenuItem onSelect={() => startAction(c, 'created')}>
                                <CircleCheck className="mr-2 h-4 w-4" /> Mark created
                              </DropdownMenuItem>
                            )}
                            {c.status !== 'dismissed' && c.status !== 'duplicate' && c.status !== 'created' && (
                              <DropdownMenuItem onSelect={() => startAction(c, 'dismiss')} className="text-red-600">
                                <Ban className="mr-2 h-4 w-4" /> Dismiss
                              </DropdownMenuItem>
                            )}
                            {(c.status === 'dismissed' || c.status === 'duplicate') && (
                              <DropdownMenuItem onSelect={() => startAction(c, 'restore')}>
                                <RotateCcw className="mr-2 h-4 w-4" /> Restore to new
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                    {expanded && (
                      <tr className="bg-gray-50/60">
                        <td />
                        <td colSpan={7} className="space-y-3 px-4 py-4 text-sm">
                          {c.match?.eid != null && (
                            <div>
                              <span className="font-medium text-gray-700">Duplicate of </span>
                              <a
                                href={listingUrl(c.match.eid)}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center gap-1 text-blue-700 hover:underline"
                              >
                                listing {c.match.eid} <ExternalLink className="h-3 w-3" />
                              </a>
                              {c.match.by && <span className="text-gray-500"> (matched by {c.match.by})</span>}
                            </div>
                          )}
                          {c.created_eid != null && (
                            <div>
                              <span className="font-medium text-gray-700">Created as </span>
                              <a
                                href={listingUrl(c.created_eid)}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center gap-1 text-blue-700 hover:underline"
                              >
                                listing {c.created_eid} <ExternalLink className="h-3 w-3" />
                              </a>
                            </div>
                          )}
                          {c.google && (
                            <div className="text-gray-600">
                              <span className="font-medium text-gray-700">Google cache: </span>
                              {[c.google.title, c.google.address].filter(Boolean).join(', ') || c.google.place_id}
                            </div>
                          )}
                          {(c.dismissed_reason || c.note) && (
                            <div className="text-gray-600">
                              <span className="font-medium text-gray-700">Note: </span>
                              {[c.dismissed_reason, c.note].filter(Boolean).join(' · ')}
                            </div>
                          )}
                          {extra.length > 0 && (
                            <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1">
                              {extra.map(([k, v]) => (
                                <Fragment key={k}>
                                  <dt className="text-gray-500">{k}</dt>
                                  <dd className="break-words text-gray-800">
                                    {typeof v === 'object' ? JSON.stringify(v) : String(v)}
                                  </dd>
                                </Fragment>
                              ))}
                            </dl>
                          )}
                          <div>
                            <div className="mb-1 font-medium text-gray-700">
                              Evidence ({evidence.length})
                              {!!c.sources?.length && (
                                <span className="ml-2 font-normal text-gray-400">via {c.sources.join(', ')}</span>
                              )}
                            </div>
                            {evidence.length === 0 ? (
                              <div className="text-gray-400">No posts recorded.</div>
                            ) : (
                              <ul className="space-y-1">
                                {evidence.map((e, i) => (
                                  <li key={`${e.url}-${i}`} className="flex flex-wrap items-center gap-2">
                                    {e.url ? (
                                      <a
                                        href={e.url}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="inline-flex items-center gap-1 text-blue-700 hover:underline"
                                      >
                                        {e.source || 'post'} <ExternalLink className="h-3 w-3" />
                                      </a>
                                    ) : (
                                      <span>{e.source || 'post'}</span>
                                    )}
                                    {e.author && <span className="text-gray-500">by {e.author}</span>}
                                    {e.at && <span className="text-gray-400">{formatDate(e.at)}</span>}
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                          <div className="text-xs text-gray-400">
                            First seen {formatDate(c.first_seen)} · last checked {formatDate(c.checked_at)}
                            {c.reviewed_at ? ` · reviewed ${formatDate(c.reviewed_at)}` : ''}
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        {!isLoading && !isError && (data?.total ?? 0) > 0 && (
          <div className="border-t border-gray-100 px-4 py-3">
            <Pagination
              total={data?.total ?? 0}
              page={page}
              limit={limit}
              itemsPerPage={limit}
              setPage={setPage}
              setItemsPerPage={(n) => {
                setLimit(n);
                setPage(1);
              }}
              showSelect
              selectOptions={[25, 50, 100]}
              compact
            />
          </div>
        )}
      </div>

      <Dialog open={!!action} onOpenChange={(v) => !v && setAction(null)}>
        <DialogContent>
          {action && (
            <>
              <DialogHeader>
                <DialogTitle>{ACTION_COPY[action.kind].title}</DialogTitle>
                <DialogDescription>
                  <strong>{action.item.name}</strong>. {ACTION_COPY[action.kind].body}
                </DialogDescription>
              </DialogHeader>
              {(action.kind === 'created' || action.kind === 'dismiss') && (
                <input
                  autoFocus
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && confirmAction()}
                  inputMode={action.kind === 'created' ? 'numeric' : undefined}
                  placeholder={action.kind === 'created' ? 'Listing eid' : 'Note (optional)'}
                  className="h-9 w-full rounded-md border border-gray-200 px-3 text-sm outline-none ring-blue-500 focus:ring-2"
                />
              )}
              <DialogFooter>
                <Button variant="outline" onClick={() => setAction(null)}>
                  Cancel
                </Button>
                <Button
                  onClick={confirmAction}
                  disabled={update.isPending || importPlace.isPending || searchPlace.isPending}
                  className={cn(action.kind === 'dismiss' && 'bg-red-600 hover:bg-red-700')}
                >
                  {(update.isPending || importPlace.isPending || searchPlace.isPending) && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
                  {ACTION_COPY[action.kind].confirm}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </MarketingPageShell>
  );
};

export default DiscoveryPage;
